import { useMemo, useRef } from "react";
import { useFrame, useThree } from "@react-three/fiber";
import * as THREE from "three";
import vert from "./shaders/home.vert.glsl";
import frag from "./shaders/home.frag.glsl";
import { Swarm, type SimParams, type Obstacle } from "./swarmSim";
import { pointerBus } from "./pointerBus";
import { wordmarkPoints } from "./wordmark";
import { FIRE_RADIAL, radialColors, hexToLinear } from "./gradient";
import { fillSafeLu, sweepCoords } from "./sweep";

/**
 * The hero: a launch grid of 1000 drones on the deck, fading to the horizon.
 * They wake dim over ~2 s, then launch in a staggered wave and fly an
 * up-and-toward arc (a lift waypoint, then the slot) under neighbor physics,
 * merging into the FREE.VET outline in a fire radial gradient, brightening
 * as they arrive. Free's storyboard, verbatim.
 */

const N = 2000;
const COLS = 50;
const ROWS = 40;
// the deck keeps its footprint no matter the fleet size: spacing is derived,
// so doubling the count packs the same patch of ground twice as densely
const GRID_W = 74.1;
const GRID_D = 64.8;
const GRID_Z0 = 4;
const WAKE_SPAN = 2.0; // every pad lit within 2 s
const LAUNCH_AT = 2.3; // first drones leave the deck
const LAUNCH_SPAN = 1.5; // launch stagger
const WORDMARK_Y = 5.0;
const WORDMARK_Z = -6;
const SETTLE_AT = 0.8; // mean journey progress that reveals the menu
const FORMED_AT = 0.97; // mean progress at which the wordmark counts as formed
/**
 * Two separate waits after the wordmark forms, and they want different
 * lengths, so they are different numbers.
 *
 * HANDOVER is how long the flight dials take to become the formed dials.
 * The last few percent of the fleet are still coasting in at that point
 * (which looks right, they should not all arrive together) and this is the
 * window they decelerate across.
 *
 * QUIET_BEFORE_PULSE is how long the finished sign holds perfectly still
 * before the choreography's first pattern fires. It is deliberately longer:
 * the swarm should read as SETTLED, not merely arrived, when the first
 * pulse hits it.
 */
const HANDOVER = 1.7;
const QUIET_BEFORE_PULSE = 4.7;

/**
 * LIGHT UNITS, the same currency as the studio's physics dials: 1 lu is one
 * drone (about 0.1 m) and 1 lu = 0.08 scene units.
 */
const LU = 0.08;
const SAFE_LAUNCH = 4.4; // lu, in transit: real flight separation on the climb
const SAFE_REST = 0.1; // lu, formed: tight enough for the letters to read clean
const SAFE_BREATH = 1.0; // lu, the open end of the endless pulse
// (the old fixed pulse is now the choreography in sweep.ts: HOLD is the 2 s
// a crossed drone stays open, WAIT the 5 s of stillness after each pattern)

/**
 * Two sets of physics dials for two different jobs.
 *
 * FLIGHT gets a drone across 60 units of deck in a couple of seconds. FORMED
 * is the studio's own tuning (10 lu/s, rigidity 6, avoidance 28, damping
 * 3.5), which is what makes the settled wordmark behave like the reference,
 * and at the flight speed cap the breath would snap outward instead of
 * swelling.
 *
 * The handover is EASED across SETTLE_GRACE rather than switched. Swapping
 * them at the instant of forming cut the speed cap by 20x in one frame, so
 * anything still on its way in was clamped mid-flight: a visible lurch. Now
 * the swarm flares to a stop, and the choreography starts the moment the
 * blend completes.
 */
const FLIGHT_SPEED = 16; // scene units/s
const FLIGHT_RIGIDITY = 4.5;
const FLIGHT_AVOIDANCE = 24;
const FLIGHT_DAMPING = 2.6;
const FORMED_SPEED = 10 * 0.08; // 10 lu/s in scene units
const FORMED_RIGIDITY = 6;
const FORMED_AVOIDANCE = 28;
const FORMED_DAMPING = 3.5;

/**
 * The pilot's earned drone cursor, flown into the swarm. It carries its own
 * safe zone and the show gets out of its way: a real body in the physics,
 * layered on top of whatever the choreography is doing.
 *
 * ESCAPE is much faster than the settled swarm's 10 lu/s, because at that
 * cap a drone simply could not clear a cursor crossing the screen; a real
 * drone being buzzed moves smartly and then drifts back.
 */
const CURSOR_SAFE = 10; // lu, the corridor it insists on while flying
/**
 * Parked, it only asks for a small bubble. Flying, drones need a whole
 * corridor to clear; hovering it is predictable, so they settle in close.
 * Without this the hole a parked cursor opens grows to the full 10 lu, which
 * reads as a bigger gap than a slow pass rather than a smaller one.
 */
const CURSOR_HOVER = 5; // lu
const CURSOR_PUSH = 600; // peak acceleration at the centre, scene units/s^2
/**
 * Escape speed rises with how fast the cursor is coming, which is both how a
 * pilot actually reacts and the only way to set the two gaps independently:
 * a slow pass and a fast one are otherwise limited by the same cap, so
 * tightening the slow gap drags the fast one down with it.
 */
const CURSOR_ESCAPE_BASE = 0.25; // scene units/s
const CURSOR_ESCAPE_GAIN = 0.3875; // per unit of cursor speed
const CURSOR_ESCAPE_MAX = 12;

/** Overall light gain on the whole sequence, deck through formation. */
const LUMA = 1.56;

function smoothstep(a: number, b: number, x: number) {
  const t = Math.min(1, Math.max(0, (x - a) / (b - a)));
  return t * t * (3 - 2 * t);
}

export function LaunchField({ onSettled }: { onSettled: () => void }) {
  const matRef = useRef<THREE.ShaderMaterial>(null);

  // captured ONCE: resizing must never rebuild a hero already in flight
  const { size } = useThree();
  const aspect0 = useRef(size.width / Math.max(1, size.height)).current;

  const world = useMemo(() => {
    const narrow = aspect0 < 1.05; // portrait: the wordmark must fit the frame
    // --- the deck: a jittered grid receding to the horizon ---
    const grid = new Float32Array(N * 3);
    const sx = GRID_W / (COLS - 1);
    const sz = GRID_D / (ROWS - 1);
    for (let r = 0; r < ROWS; r++) {
      for (let c = 0; c < COLS; c++) {
        const i = r * COLS + c;
        grid[i * 3] = (c - (COLS - 1) / 2) * sx + (Math.random() - 0.5) * sx * 0.37;
        grid[i * 3 + 1] = 0;
        grid[i * 3 + 2] = GRID_Z0 - r * sz + (Math.random() - 0.5) * sz * 0.3;
      }
    }

    // --- the wordmark: FREE.VET outline, hung in the air facing camera ---
    const wmLocal = wordmarkPoints(N, narrow ? 9 : 15, narrow ? 2.6 : 3.6);
    const fireLocal = radialColors(wmLocal, FIRE_RADIAL);
    const wm = new Float32Array(N * 3);
    for (let i = 0; i < N; i++) {
      wm[i * 3] = wmLocal[i * 3];
      wm[i * 3 + 1] = wmLocal[i * 3 + 1] + WORDMARK_Y;
      wm[i * 3 + 2] = WORDMARK_Z;
    }

    // --- assignment: rank-pair by x so letters gather left-to-right ---
    // (near and far rows mix into each glyph; no long crossings)
    const gOrd = Array.from({ length: N }, (_, i) => i).sort(
      (a, b) => grid[a * 3] - grid[b * 3] || grid[a * 3 + 2] - grid[b * 3 + 2]
    );
    const wOrd = Array.from({ length: N }, (_, i) => i).sort(
      (a, b) => wm[a * 3] - wm[b * 3] || wm[a * 3 + 1] - wm[b * 3 + 1]
    );
    const slotOf = new Int32Array(N); // drone -> its wordmark point
    for (let k = 0; k < N; k++) slotOf[gOrd[k]] = wOrd[k];

    // --- per-drone choreography data ---
    const seeds = new Float32Array(N);
    const wakeAt = new Float32Array(N);
    const launchAt = new Float32Array(N);
    const impulse = new Float32Array(N);
    const stage = new Uint8Array(N); // 0 deck | 1 lift | 2 slot
    const dist0 = new Float32Array(N);
    const lift = new Float32Array(N * 3);
    const gridCol = new Float32Array(N * 3);
    const fireCol = new Float32Array(N * 3);
    const amber = hexToLinear("#ffa040");
    for (let i = 0; i < N; i++) {
      seeds[i] = Math.random();
      wakeAt[i] = Math.random() * WAKE_SPAN;
      launchAt[i] = LAUNCH_AT + Math.random() * LAUNCH_SPAN;
      impulse[i] = 3 + Math.random() * 2;

      const s = slotOf[i];
      const gx = grid[i * 3], gz = grid[i * 3 + 2];
      const wx = wm[s * 3], wy = wm[s * 3 + 1], wz = wm[s * 3 + 2];
      lift[i * 3] = gx + (wx - gx) * 0.3;
      lift[i * 3 + 1] = 4.2 + Math.random() * 3.2;
      lift[i * 3 + 2] = gz + (wz - gz) * 0.45;
      dist0[i] = Math.max(2, Math.hypot(wx - gx, wy, wz - gz));

      const j = 0.7 + Math.random() * 0.5; // pad-to-pad warmth variance
      gridCol[i * 3] = amber[0] * j;
      gridCol[i * 3 + 1] = amber[1] * j;
      gridCol[i * 3 + 2] = amber[2] * j;
      fireCol[i * 3] = fireLocal[s * 3];
      fireCol[i * 3 + 1] = fireLocal[s * 3 + 1];
      fireCol[i * 3 + 2] = fireLocal[s * 3 + 2];
    }

    // the choreography reads each drone's SLOT, not its live position, so a
    // wavefront stays geometrically clean even while the swarm is moving
    const slotPos = new Float32Array(N * 3);
    for (let i = 0; i < N; i++) {
      const s = slotOf[i] * 3;
      slotPos[i * 3] = wm[s];
      slotPos[i * 3 + 1] = wm[s + 1];
      slotPos[i * 3 + 2] = wm[s + 2];
    }

    const sim = new Swarm(grid);
    const colBuf = new Float32Array(N * 3); // starts dark; wake ramps it in
    return {
      sim, wm, slotOf, seeds, wakeAt, launchAt, impulse, stage, dist0, lift,
      gridCol, fireCol, colBuf,
      coords: sweepCoords(slotPos),
      radLu: new Float32Array(N), // per-drone safe zone, light units
      radScene: new Float32Array(N), // the same, in scene units, for the sim
    };
  }, [aspect0]);

  const geometry = useMemo(() => {
    const g = new THREE.BufferGeometry();
    g.setAttribute("position", new THREE.BufferAttribute(world.sim.pos, 3));
    g.setAttribute("aColor", new THREE.BufferAttribute(world.colBuf, 3));
    g.setAttribute("aSeed", new THREE.BufferAttribute(world.seeds, 1));
    g.boundingSphere = new THREE.Sphere(new THREE.Vector3(0, 3, -25), 90);
    return g;
  }, [world]);

  // twice the fleet on the same footprint means twice the light per letter,
  // so each LED is a touch smaller and the wordmark stays crisp
  const uniforms = useMemo(() => ({ uTime: { value: 0 }, uSize: { value: 6 } }), []);

  const t0 = useRef(-1);
  const settled = useRef(false);
  const formed = useRef(false);
  const pulseT = useRef(0);
  const params = useRef<SimParams>({
    safe: SAFE_LAUNCH * LU,
    maxSpeed: FLIGHT_SPEED,
    rigidity: FLIGHT_RIGIDITY,
    avoidance: FLIGHT_AVOIDANCE,
    damping: FLIGHT_DAMPING,
  });
  const cursor = useRef<Obstacle>({
    x: 0, y: 0, z: WORDMARK_Z,
    r: CURSOR_SAFE * LU,
    push: CURSOR_PUSH,
    speed: CURSOR_ESCAPE_BASE,
    ux: 1, uy: 0, uz: 0,
    moving: false,
  });
  const ray = useRef(new THREE.Vector3());
  const hadCursor = useRef(false);

  useFrame((state, dt) => {
    const m = matRef.current;
    if (!m) return;
    if (t0.current < 0) t0.current = state.clock.elapsedTime;
    const t = state.clock.elapsedTime - t0.current;
    m.uniforms.uTime.value = state.clock.elapsedTime;

    const { sim, wm, slotOf, wakeAt, launchAt, impulse, stage, dist0, lift, gridCol, fireCol, colBuf,
      coords, radLu, radScene } = world;
    const { pos, vel, tgt } = sim;

    // launches + lift-to-slot handoff
    for (let i = 0; i < N; i++) {
      const i3 = i * 3;
      if (stage[i] === 0) {
        if (t >= launchAt[i]) {
          stage[i] = 1;
          tgt[i3] = lift[i3];
          tgt[i3 + 1] = lift[i3 + 1];
          tgt[i3 + 2] = lift[i3 + 2];
          vel[i3 + 1] += impulse[i];
        }
      } else if (stage[i] === 1) {
        const dx = pos[i3] - lift[i3], dy = pos[i3 + 1] - lift[i3 + 1], dz = pos[i3 + 2] - lift[i3 + 2];
        if (dx * dx + dy * dy + dz * dz < 2.5 * 2.5) {
          stage[i] = 2;
          const s = slotOf[i] * 3;
          tgt[i3] = wm[s];
          tgt[i3 + 1] = wm[s + 1];
          tgt[i3 + 2] = wm[s + 2];
        }
      }
    }

    // journey progress -> brightness, color, settle detection
    let total = 0;
    for (let i = 0; i < N; i++) {
      const i3 = i * 3;
      const wake = Math.min(1, Math.max(0, (t - wakeAt[i]) / 0.45));
      let e = 0;
      if (stage[i] > 0) {
        const s = slotOf[i] * 3;
        const d = Math.hypot(wm[s] - pos[i3], wm[s + 1] - pos[i3 + 1], wm[s + 2] - pos[i3 + 2]);
        const progress = 1 - Math.min(1, d / dist0[i]);
        total += progress;
        e = smoothstep(0.08, 0.9, progress);
      }
      const b = wake * (0.13 + 0.87 * e) * LUMA;
      colBuf[i3] = (gridCol[i3] + (fireCol[i3] - gridCol[i3]) * e) * b;
      colBuf[i3 + 1] = (gridCol[i3 + 1] + (fireCol[i3 + 1] - gridCol[i3 + 1]) * e) * b;
      colBuf[i3 + 2] = (gridCol[i3 + 2] + (fireCol[i3 + 2] - gridCol[i3 + 2]) * e) * b;
    }
    const mean = total / N;

    // tighten the safe zone as the letters assemble (outline spacing is finer
    // than deck spacing; keeping flight-safe distance would jostle forever)
    const settleK = smoothstep(0.55, 0.95, mean);
    let safeLu = SAFE_LAUNCH + (SAFE_REST - SAFE_LAUNCH) * settleK;

    // then the formed wordmark BREATHES forever: 2 s tight, 2 s open. The
    // clock is a frame accumulator, never a timer, so it holds nothing up
    // and there is nothing to leak or cancel.
    if (!formed.current && mean > FORMED_AT) formed.current = true;
    // once formed, the choreography takes over: a wavefront opens each drone
    // it crosses. It holds still for SETTLE_GRACE first, so the stragglers
    // finish landing before the sign starts breathing.
    let perDrone: Float32Array | undefined;
    if (formed.current) {
      pulseT.current += dt;
      safeLu = SAFE_REST;

      // hand the flight dials over to the formed ones, easing rather than
      // switching: a hard swap cut the speed cap 20x in one frame and
      // clamped everything still inbound
      const h = smoothstep(0, HANDOVER, pulseT.current);
      params.current.maxSpeed = FLIGHT_SPEED + (FORMED_SPEED - FLIGHT_SPEED) * h;
      params.current.rigidity = FLIGHT_RIGIDITY + (FORMED_RIGIDITY - FLIGHT_RIGIDITY) * h;
      params.current.avoidance = FLIGHT_AVOIDANCE + (FORMED_AVOIDANCE - FLIGHT_AVOIDANCE) * h;
      params.current.damping = FLIGHT_DAMPING + (FORMED_DAMPING - FLIGHT_DAMPING) * h;

      const pt = pulseT.current - QUIET_BEFORE_PULSE;
      if (pt >= 0) {
        fillSafeLu(pt, coords, radLu, SAFE_REST, SAFE_BREATH);
        for (let i = 0; i < N; i++) radScene[i] = radLu[i] * LU;
        perDrone = radScene;
      }
    }
    // the drone cursor, if the pilot has earned it: cast its screen position
    // onto the wordmark's plane so it becomes a body the swarm must avoid
    let obstacle: Obstacle | undefined;
    if (pointerBus.active) {
      const cam = state.camera;
      const v = ray.current;
      v.set(
        (pointerBus.x / state.size.width) * 2 - 1,
        -(pointerBus.y / state.size.height) * 2 + 1,
        0.5
      )
        .unproject(cam)
        .sub(cam.position);
      const k = Math.abs(v.z) > 1e-6 ? (WORDMARK_Z - cam.position.z) / v.z : -1;
      if (k > 0) {
        const o = cursor.current;
        const nx = cam.position.x + v.x * k;
        const ny = cam.position.y + v.y * k;
        // heading, from how far it moved this frame (per second, so the
        // "is it moving" test does not depend on the frame rate)
        const sx = (nx - o.x) / Math.max(dt, 1e-4);
        const sy = (ny - o.y) / Math.max(dt, 1e-4);
        const sp = Math.hypot(sx, sy);
        o.moving = hadCursor.current && sp > 0.6;
        if (o.moving) { o.ux = sx / sp; o.uy = sy / sp; o.uz = 0; }
        // a corridor while flying, a small bubble while parked
        o.r = (o.moving ? CURSOR_SAFE : CURSOR_HOVER) * LU;
        o.speed = Math.min(
          CURSOR_ESCAPE_MAX,
          CURSOR_ESCAPE_BASE + CURSOR_ESCAPE_GAIN * (o.moving ? sp : 0)
        );
        o.x = nx;
        o.y = ny;
        hadCursor.current = true;
        obstacle = o;
      }
    } else {
      hadCursor.current = false;
    }

    params.current.safe = safeLu * LU;
    sim.step(dt, params.current, perDrone, obstacle);

    if (!settled.current && mean > SETTLE_AT) {
      settled.current = true;
      onSettled();
    }

    (geometry.getAttribute("position") as THREE.BufferAttribute).needsUpdate = true;
    (geometry.getAttribute("aColor") as THREE.BufferAttribute).needsUpdate = true;
  });

  return (
    <points name="heroswarm" geometry={geometry} frustumCulled={false}>
      <shaderMaterial
        ref={matRef}
        vertexShader={vert}
        fragmentShader={frag}
        uniforms={uniforms}
        transparent
        depthWrite={false}
        depthTest={false}
        blending={THREE.AdditiveBlending}
      />
    </points>
  );
}
